/**
 * Client de l'API du bail et du loyer — L6.
 *
 * **Aucun chemin de ce module n'entre dans la liste blanche du service worker** (invariant 17).
 * Tous portent un montant, une identité ou une pièce. C'est vrai deux fois du téléchargement d'une
 * quittance : servie depuis `/api/*`, elle est en `NetworkOnly` par construction, et le serveur y
 * ajoute `no-store` — une quittance ne doit pas rester dans le cache d'un navigateur partagé.
 *
 * **Les montants arrivent en nombres, jamais en chaînes formatées.** Le formatage se fait à
 * l'affichage, par `Intl` (règle 9 du §6 de la conception L10) : un échéancier est un tableau de
 * nombres, c'est le pire endroit pour un format figé en français dans une interface arabe.
 */

import { appeler, telecharger } from './api'

export type StatutBail = 'Draft' | 'PendingSignature' | 'Active' | 'Terminated' | 'Expired' | 'Cancelled'
export type TypeBail = 'SingleJoint' | 'Individual'
export type RoleAuBail = 'Tenant' | 'Landlord' | 'Mandatary'
export type StatutEnregistrement = 'NotRegistered' | 'Recommended' | 'Registered'
export type StatutDemarche = 'Pending' | 'Done' | 'NotApplicable'
export type StatutPeriode = 'Upcoming' | 'PartiallySettled' | 'Settled' | 'Late' | 'Cancelled'

export type StatutEcheance =
  | 'Upcoming'
  | 'Due'
  | 'DeclaredUnconfirmed'
  | 'Confirmed'
  | 'Disputed'
  | 'Late'
  | 'PartiallyPaid'
  | 'Waived'
  | 'Cancelled'

export type MoyenPaiement = 'Cash' | 'BankTransfer' | 'Cheque' | 'Other'
export type ValeurVote = 'NotCast' | 'For' | 'Against' | 'Neutral'
export type ResultatVote = 'Pending' | 'Favorable' | 'Unfavorable' | 'Split' | 'NoQuorum' | 'Cancelled'

/** Les quatre démarches de la loi 67-12 (spec §9.1-5). Liste fermée, côté serveur comme ici. */
export const DEMARCHES = ['WrittenContract', 'EntryInventory', 'Registration', 'DepositCap'] as const

export interface Bail {
  id: string
  propertyId: string
  unitId: string
  /** Le quartier seul. La ville est `propertyCityCode`, à lire dans le référentiel (`useVille().libelle`). */
  propertyLabel: string | null
  propertyCityCode: string | null
  landlordUserId: string
  landlordDisplayName: string | null
  status: StatutBail
  startDate: string
  endDate: string
  rentAmount: number
  chargesAmount: number
  currencyCode: string
  paymentDueDayOfMonth: number
  tenantCount: number
  /** Ce bail attend MA confirmation : la signature est bilatérale (conception L6, §1.4). */
  awaitingMyConfirmation: boolean
  /** Ce que la carte « Mes baux » montre sans ouvrir le bail (15/09/2026). */
  type: TypeBail
  securityDeposit: number
  noticePeriodDays: number
  isJointlyLiable: boolean
  /** Les locataires en place, par leur nom d'affichage. */
  tenantDisplayNames: string[]
  confirmedTenantCount: number
}

export interface PartieAuBail {
  id: string
  userId: string
  displayName: string | null
  role: RoleAuBail
  rentShare: number
  isJointlyLiable: boolean
  /** Un locataire confirme ; le bailleur, lui, déclare (`signedOn` sur le bail). Toujours nul pour lui. */
  confirmedAt: string | null
  joinedOn: string
  /** Une partie sortie reste dans la liste : le bail l'a connue. */
  leftOn: string | null
}

/** Une démarche de la loi 67-12 : un fait déclaré, daté et attribué — les deux derniers nuls tant qu'elle n'est pas faite. */
export interface DemarcheBail {
  itemCode: string
  status: StatutDemarche
  note: string | null
  completedAt: string | null
  /** Toujours une partie au bail : c'est ainsi qu'on le nomme sans second appel. */
  completedByUserId: string | null
}

/** Le modèle engendré : sa langue, sa date, et provisoire tant que le gabarit n'est pas relu juridiquement. */
export interface ModeleBail {
  language: string
  generatedAt: string
  isProvisional: boolean
}

export interface DetailBail {
  lease: Bail
  householdId: string | null
  signedOn: string | null
  signedAt: string | null
  securityDeposit: number
  noticePeriodDays: number
  registrationStatus: StatutEnregistrement
  hasGeneratedModel: boolean
  generatedModel: ModeleBail | null
  parties: PartieAuBail[]
  checklist: DemarcheBail[]
}

export interface Echeance {
  id: string
  periodId: string
  leaseId: string
  dueDate: string
  payerUserId: string
  payerDisplayName: string | null
  rentPart: number
  chargesPart: number
  totalAmount: number
  currencyCode: string
  status: StatutEcheance
  /** Vrai seulement sur une échéance confirmée par les deux parties (spec §10.3). */
  countsTowardReputation: boolean
  settledOn: string | null
  pendingDeclarationId: string | null
  /**
   * Qui a déclaré, quand une déclaration attend.
   *
   * C'est ce qui permet d'afficher « confirmer » à l'un et « en attente » à l'autre. Sans lui, les
   * deux parties verraient le même bouton et l'invariant « le confirmant n'est jamais le
   * déclarant » deviendrait une erreur qu'on découvre en cliquant.
   */
  pendingDeclaredByUserId: string | null
  /**
   * La déclaration qui compte — en attente, sinon la dernière confirmée ou contestée. Nulles tant
   * que rien n'a été déclaré. C'est ce que l'échéancier montre en « Payé le » et « Moyen ».
   */
  declaredPaidOn: string | null
  declaredMethod: MoyenPaiement | null
  receiptId: string | null
}

export interface PeriodeLoyer {
  id: string
  periodStart: string
  periodEnd: string
  dueDate: string
  totalExpected: number
  currencyCode: string
  isProrated: boolean
  status: StatutPeriode
  installments: Echeance[]
}

export interface Quittance {
  id: string
  number: string
  issuedAt: string
  periodLabel: string
  totalAmount: number
  currencyCode: string
  tenantUserId: string
  landlordUserId: string
  /** Faux pendant les quelques secondes qui séparent la numérotation du rendu (D12). */
  isRendered: boolean
  cancelledAt: string | null
  /**
   * Ce que le document dit — pour l'aperçu, sans second appel. Les noms et le libellé du bien
   * sont figés à l'émission : une quittance ne suit pas les renommages.
   */
  language: string
  rentAmount: number
  chargesAmount: number
  periodStart: string
  periodEnd: string
  paidOn: string
  method: MoyenPaiement
  tenantDisplayName: string
  landlordDisplayName: string
  propertyLabel: string
  /** Empreinte hexadécimale du PDF ; nulle tant qu'il n'est pas rendu. */
  contentHash: string | null
}

export interface TraitVote {
  traitCode: string
  label: string
  selfValue: number | null
}

/**
 * La fiche que voit un colocataire appelé à voter.
 *
 * **Volontairement pauvre** : ni tranche de revenu, ni garant, ni employeur, ni établissement
 * (conception L6, §1.2-c). Le dossier sert à candidater auprès d'un bailleur, pas à être parcouru
 * par trois colocataires.
 */
export interface FicheVote {
  sessionId: string
  applicationId: string
  openedAt: string
  closesAt: string
  /** L'issue, une fois close ; `Pending` tant que la consultation court. */
  outcome: ResultatVote
  /** Le compte du candidat : c'est lui qui porte sa réputation de colocataire. */
  seekerUserId: string
  seekerDisplayName: string | null
  seekerAge: number | null
  occupation: string | null
  moveInWindowStart: string | null
  moveInWindowEnd: string | null
  desiredDurationMonths: number | null
  motivationMessage: string | null
  lifestyleTraits: TraitVote[]
  myVote: ValeurVote
  myComment: string | null
}

/**
 * Le décompte, tel que le propriétaire le lit.
 *
 * **Les commentaires ne portent pas leur auteur** (conception L6, §1.2-d). L'attribution reste en
 * base pour l'audit ; elle ne sort que par le back-office.
 */
export interface ResultatVoteFoyer {
  sessionId: string
  outcome: ResultatVote
  closesAt: string
  quorumRequired: number
  eligibleCount: number
  forCount: number
  againstCount: number
  neutralCount: number
  notCastCount: number
  comments: string[]
}

/**
 * Une consultation où je suis votant — en cours ou close (15/09/2026), avec ce que je lis du
 * candidat (jamais son revenu, son garant ni son employeur — D28), ma réponse et l'issue du foyer.
 */
export interface VoteEnAttente {
  sessionId: string
  applicationId: string
  householdId: string
  listingId: string | null
  listingTitle: string | null
  openedAt: string
  closesAt: string
  outcome: ResultatVote
  seekerDisplayName: string | null
  seekerAge: number | null
  occupation: string | null
  motivationMessage: string | null
  myVote: ValeurVote
  myComment: string | null
  votedAt: string | null
}

interface Page<T> {
  items: T[]
  page: number
  size: number
  total: number
}

// ---------------------------------------------------------------------------
// Bail
// ---------------------------------------------------------------------------

export function mesBaux(asLandlord: boolean): Promise<Page<Bail>> {
  return appeler<Page<Bail>>(`/me/leases?asLandlord=${asLandlord}`)
}

export function bail(id: string): Promise<DetailBail> {
  return appeler<DetailBail>(`/leases/${id}`)
}

export function declarerSigne(id: string, signedOn: string): Promise<void> {
  return appeler<void>(`/leases/${id}/declare-signed`, {
    method: 'POST',
    body: JSON.stringify({ signedOn }),
  })
}

export function confirmerBail(id: string): Promise<{ status: StatutBail }> {
  return appeler<{ status: StatutBail }>(`/leases/${id}/confirm`, { method: 'POST' })
}

export function resilierBail(id: string, reasonCode: string, effectiveOn: string): Promise<void> {
  return appeler<void>(`/leases/${id}/terminate`, {
    method: 'POST',
    body: JSON.stringify({ reasonCode, effectiveOn }),
  })
}

export function marquerDemarche(
  id: string,
  itemCode: string,
  status: StatutDemarche,
  note: string | null,
): Promise<void> {
  return appeler<void>(`/leases/${id}/checklist/${itemCode}`, {
    method: 'PUT',
    body: JSON.stringify({ status, note }),
  })
}

export function engendrerModele(id: string, language: string): Promise<void> {
  return appeler<void>(`/leases/${id}/model`, {
    method: 'POST',
    body: JSON.stringify({ language }),
  })
}

export function echeancier(id: string): Promise<PeriodeLoyer[]> {
  return appeler<PeriodeLoyer[]>(`/leases/${id}/schedule`)
}

// ---------------------------------------------------------------------------
// Echeances et paiements
// ---------------------------------------------------------------------------

export function mesEcheances(asLandlord: boolean, openOnly: boolean): Promise<Page<Echeance>> {
  return appeler<Page<Echeance>>(
    `/me/installments?asLandlord=${asLandlord}&openOnly=${openOnly}&size=50`,
  )
}

export function declarerPaiement(
  installmentId: string,
  paidOn: string,
  amount: number,
  method: MoyenPaiement,
  referenceNote: string | null,
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/installments/${installmentId}/declare`, {
    method: 'POST',
    body: JSON.stringify({ paidOn, amount, method, referenceNote }),
  })
}

export function confirmerPaiement(declarationId: string): Promise<{ receiptId: string }> {
  return appeler<{ receiptId: string }>(`/declarations/${declarationId}/confirm`, { method: 'POST' })
}

export function contesterPaiement(
  declarationId: string,
  reasonCode: string,
  note: string | null,
): Promise<void> {
  return appeler<void>(`/declarations/${declarationId}/dispute`, {
    method: 'POST',
    body: JSON.stringify({ reasonCode, note }),
  })
}

// ---------------------------------------------------------------------------
// Quittances
// ---------------------------------------------------------------------------

export function mesQuittances(): Promise<Page<Quittance>> {
  return appeler<Page<Quittance>>('/me/receipts?size=50')
}

/**
 * Télécharge une quittance. Un `fetch` authentifié, jamais un `<a href>` : le jeton d'accès
 * vit en mémoire, un lien nu partirait sans lui (voir `telecharger`).
 */
export function telechargerQuittance(id: string, numero: string): Promise<void> {
  return telecharger(`/receipts/${id}/download`, `quittance-${numero}.pdf`)
}

/** Télécharge le modèle de bail engendré pour ce bail. */
export function telechargerModeleBail(leaseId: string): Promise<void> {
  return telecharger(`/leases/${leaseId}/model`, 'modele-de-bail.pdf')
}

// ---------------------------------------------------------------------------
// Vote du foyer
// ---------------------------------------------------------------------------

export function mesVotes(): Promise<VoteEnAttente[]> {
  return appeler<VoteEnAttente[]>('/me/household-votes')
}

export function ficheVote(sessionId: string): Promise<FicheVote> {
  return appeler<FicheVote>(`/household-votes/${sessionId}`)
}

export function voter(sessionId: string, value: ValeurVote, privateComment: string | null): Promise<void> {
  return appeler<void>(`/household-votes/${sessionId}/vote`, {
    method: 'POST',
    body: JSON.stringify({ value, privateComment }),
  })
}

export function resultatVote(applicationId: string): Promise<ResultatVoteFoyer> {
  return appeler<ResultatVoteFoyer>(`/applications/${applicationId}/household-vote`)
}

export function ouvrirVote(applicationId: string): Promise<{ sessionId: string | null }> {
  return appeler<{ sessionId: string | null }>(`/applications/${applicationId}/household-vote`, {
    method: 'POST',
  })
}
